import React, { memo } from "react";
import clsx from "clsx";
import type { PriceConfigType, PriceType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { useCarConfigSelector } from "../../../../redux/store";
import {
    formatAEMCashPriceIntl,
    getCashPriceDisclaimerLabel,
} from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import styles from "./CashPrice.module.scss";
import { hideDiscountInfo } from "../../../../../../../common-deprecated/priceUtils";
import PromotionNumbers from "./PromotionNumbers";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import Popover from "../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import { CarConfigLabel } from "../../../../utils/labelUtils";

type CashPricePropsType = {
    price: PriceType;
    priceConfig: PriceConfigType;
    showDiscountPrice?: boolean; // Use the discount price instead of the cash price to show the price with discounts
    showZeroPrice?: boolean;
    showDiscountHorizontal?: boolean; // Used to show the discount price horizontally instead of the standard vertical layout
    showCashDisclaimer?: boolean;
    dualCurrencyColumn?: boolean; // Show the dual currency price in a column layout
};

const CashPrice = (props: CashPricePropsType): JSX.Element | null => {
    const {
        price,
        priceConfig,
        showDiscountPrice = true,
        showZeroPrice,
        showDiscountHorizontal,
        showCashDisclaimer = true,
        dualCurrencyColumn,
    } = props;
    const { licenseFee, promotions, highlight } = price;
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const cashPriceDisclaimerPopoverLabel = useCarConfigSelector((state) =>
        getCashPriceDisclaimerLabel(state.car.model.labels),
    );

    const showTooltip =
        !!cashPriceDisclaimerPopoverLabel &&
        showCashDisclaimer &&
        (commonSettings.onlyShowCashDisclaimerWithLicenseFee ? licenseFee > 0 : true);

    // Use the discount price instead of the cash price to show the price with discounts
    const priceValue = showDiscountPrice ? price.discount : price.cash;

    if (priceValue == null || (!priceValue && !showZeroPrice) || !priceConfig.priceEnabled) return null;

    const hideDiscount = hideDiscountInfo(priceConfig);

    if ((highlight || price.discount !== price.cash || promotions.length > 0) && !hideDiscount) {
        const { primaryPrice: primaryDiscountPrice, secondaryPrice: secondaryDiscountPrice } = formatAEMCashPriceIntl(
            price.discount,
            commonSettings,
            priceConfig,
            licenseFee,
            true,
        );
        const { primaryPrice: primaryCashPrice, secondaryPrice: secondaryCashPrice } = formatAEMCashPriceIntl(
            price.cash,
            commonSettings,
            priceConfig,
            licenseFee,
            true,
        );
        const showStrikedPrice = !highlight && price.discount !== price.cash;
        return (
            <>
                <span
                    className={clsx(
                        styles.pricewrapper,
                        (!showStrikedPrice || showDiscountHorizontal || secondaryDiscountPrice) && styles.flex,
                    )}
                >
                    <span dangerouslySetInnerHTML={{ __html: primaryDiscountPrice }} />
                    <span className={styles.promo}>
                        {showStrikedPrice && (
                            <span
                                className={styles.strikethrough}
                                dangerouslySetInnerHTML={{ __html: primaryCashPrice }}
                            />
                        )}
                        {promotions.length > 0 && !secondaryDiscountPrice && (
                            <PromotionNumbers promotions={promotions} />
                        )}
                    </span>
                </span>

                {secondaryDiscountPrice && secondaryCashPrice && (
                    <>
                        <span className={styles.divider}>/</span>
                        <span
                            className={clsx(
                                styles.pricewrapper,
                                (!showStrikedPrice || showDiscountHorizontal || secondaryDiscountPrice) && styles.flex,
                            )}
                        >
                            <span dangerouslySetInnerHTML={{ __html: secondaryDiscountPrice }} />
                            <span className={styles.promo}>
                                {showStrikedPrice && (
                                    <span
                                        className={styles.strikethrough}
                                        dangerouslySetInnerHTML={{ __html: secondaryCashPrice }}
                                    />
                                )}
                                {promotions.length > 0 && <PromotionNumbers promotions={promotions} />}
                            </span>
                        </span>
                    </>
                )}
            </>
        );
    }

    const { primaryPrice, secondaryPrice } = formatAEMCashPriceIntl(
        priceValue,
        commonSettings,
        priceConfig,
        licenseFee,
        true,
    );

    return (
        <span className={styles.wrapper}>
            <span className={styles.pricewrapper}>
                <span dangerouslySetInnerHTML={{ __html: primaryPrice }} />
                {secondaryPrice && (
                    <>
                        {!dualCurrencyColumn && <span className="price-divider">/</span>}
                        <span dangerouslySetInnerHTML={{ __html: secondaryPrice }} />
                    </>
                )}
            </span>
            {showTooltip && (
                <Popover placement="top">
                    <Popover.Trigger>
                        <Icon variant="info" sizing="md" aria-hidden="true" />
                        <span className={a11y["sr-only"]}>
                            <CarConfigLabel label="carConfigA11yButtonTogglePriceDisclaimer" />
                        </span>
                    </Popover.Trigger>
                    <Popover.Content>
                        <div dangerouslySetInnerHTML={{ __html: cashPriceDisclaimerPopoverLabel }} />
                    </Popover.Content>
                </Popover>
            )}
        </span>
    );
};

export default memo(CashPrice);
